import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
} from '@mui/material';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useGetContractBySellerQuery,
  useGetContractQuery,
} from '@/apis/extended/contractApi';
import {
  useSignContractAsBuyerMutation,
  useSignContractAsSellerMutation,
} from '@/apis/extended/contractRatificationApi';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { passedAcceptanceDeadline } from '@/utils/acceptanceDeadline';
import { parseDateOnly } from '@/utils/dateOnly';
import { CommonPrevNextButtons } from '../Common/CommonContract/CommonPrevNextButtons';
import { CommonFormControl } from '../Common/CommonFormControl';
import UserSignatureCanvas from '../Contract/Components/SignatureCanvas/UserSignatureCanvas';

const initialForm = {
  name: '',
  signature: '',
};

type ContractDetailSellerPostNegotiationSignProps = {
  uid: string;
  /** Who is signing. The buyer signs first, then the seller. */
  role?: 'buyer' | 'seller';
};

export const ContractDetailSellerPostNegotiationSign = ({
  uid,
  role = 'seller',
}: ContractDetailSellerPostNegotiationSignProps) => {
  const isBuyer = role === 'buyer';
  const navigate = useNavigate();
  const sellerQuery = useGetContractBySellerQuery(uid, { skip: isBuyer });
  const buyerQuery = useGetContractQuery(uid, { skip: !isBuyer });
  const { data: contract, isLoading: isLoadingContract } = isBuyer
    ? buyerQuery
    : sellerQuery;
  const [signAsSeller, { isLoading: isSigningSeller }] =
    useSignContractAsSellerMutation();
  const [signAsBuyer, { isLoading: isSigningBuyer }] =
    useSignContractAsBuyerMutation();
  const signContract = isBuyer ? signAsBuyer : signAsSeller;
  const isSigning = isSigningSeller || isSigningBuyer;
  const mySign = isBuyer ? contract?.buyerSign : contract?.sellerSign;

  const [form, setForm] = useState(initialForm);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!mySign) return;
    setForm({
      name: mySign.name || '',
      signature: mySign.signature || '',
    });
  }, [mySign]);

  const isValid = useMemo(() => {
    return Boolean(form.name && form.signature);
  }, [form]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!isValid || !contract?._id) return;

    setSubmitError(null);
    try {
      const payload = {
        contractId: contract._id.toString(),
        name: form.name,
        signature: form.signature,
      };

      await signContract(payload).unwrap();

      toast.success(
        isBuyer
          ? 'Contract signed. The seller signs next.'
          : 'Contract signed successfully! The contract is now fully ratified.'
      );

      navigate(
        `/${
          isBuyer ? 'buyer' : 'seller'
        }/property-dashboard/${contract._id.toString()}`
      );
    } catch (error: any) {
      console.error('❌ Error signing contract:', error);
      console.error('Error details:', {
        status: error?.status,
        data: error?.data,
        message: error?.message,
        fullError: error,
      });

      // `message` carries the reason; `error` is only the generic summary.
      const reason =
        error?.data?.message ||
        error?.data?.error ||
        'Failed to sign contract.';
      const errorMessage =
        error?.status === 409
          ? `${reason} Your signature was not saved.`
          : `${reason} Your signature was not saved. Please try again.`;
      setSubmitError(errorMessage);
      toast.error(errorMessage);
    }
  };

  if (isLoadingContract) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading contract...</Typography>
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Contract not found</Typography>
      </Box>
    );
  }

  // Check if contract is in the right status
  const canSign =
    contract.contractStatus === 'awaiting_signatures' ||
    contract.contractStatus === 'awaiting_seller_signature' ||
    (isBuyer && Boolean(mySign?.isStored));

  if (!canSign) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Alert className="border-blue-500/50">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Contract Not Ready for Signature</AlertTitle>
          <AlertDescription>
            This contract is currently in status:{' '}
            <strong>{contract.contractStatus}</strong>
            <br />
            The contract must be in "awaiting_signatures" or
            "awaiting_seller_signature" status before you can sign.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (mySign?.isStored) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Alert className="border-green-500/50 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle>Already Signed</AlertTitle>
          <AlertDescription>
            You have already signed this contract.
            {contract.isRatified ? (
              <span> The contract is now fully ratified!</span>
            ) : (
              <span>
                {' '}
                Waiting for the {isBuyer ? 'seller' : 'buyer'}'s signature to
                complete ratification.
              </span>
            )}
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  // The buyer signs first; until then the server refuses the seller's signature.
  // Mirrors the server: once the written-acceptance date has passed, nobody
  // can sign until a new Offer Expiration Date is agreed.
  const acceptanceDeadline = passedAcceptanceDeadline(contract);
  if (acceptanceDeadline) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Acceptance Deadline Has Passed</AlertTitle>
          <AlertDescription>
            Written acceptance was due by{' '}
            {acceptanceDeadline.toLocaleDateString('en-US')}, so this contract
            can't be signed yet. Propose a new date from the negotiation page
            (the Acceptance deadline notice at the top); once the other side
            accepts it, the agent finalizes again and signing reopens.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (!isBuyer && !contract.buyerSign?.isStored) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Alert className="border-blue-500/50">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Waiting for the Buyer to Sign</AlertTitle>
          <AlertDescription>
            The contract is ready for signatures. The buyer signs first; you can
            sign as soon as they have.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  const closingDate = parseDateOnly(
    contract.offerSpecifics?.closingDate || contract.timeline?.closingDate
  );
  const isPositive = (n: unknown): n is number =>
    typeof n === 'number' && n > 0;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Sign Contract After Negotiations"
          avatar={<i className="fa-light fa-file-signature" />}
        />
        <CardContent>
          <Alert className="mb-6 border-green-500/50 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle>Contract Ready for Your Signature</AlertTitle>
            <AlertDescription>
              All negotiations have been completed and the contract has been
              finalized.
              {isBuyer && (
                <span>
                  {' '}
                  You sign first; the seller's signature then ratifies the
                  contract.
                </span>
              )}
              {!isBuyer &&
                contract.contractStatus === 'awaiting_seller_signature' &&
                contract.buyerSign?.isStored && (
                  <span>
                    {' '}
                    The buyer has already signed. Your signature will complete
                    the ratification.
                  </span>
                )}
            </AlertDescription>
          </Alert>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Contract Summary
          </Typography>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Purchase Price:</strong> $
              {contract.offerSpecifics?.offerPrice?.toLocaleString() || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Closing Date:</strong>{' '}
              {closingDate ? closingDate.toLocaleDateString('en-US') : 'N/A'}
            </Typography>
            {isPositive(contract.offerSpecifics?.earnestMoneyDeposit) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Earnest Money:</strong> $
                {contract.offerSpecifics.earnestMoneyDeposit.toLocaleString()}
              </Typography>
            )}
            {isPositive(contract.offerSpecifics?.buyerAgentCompensation) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Buyer Agent Compensation:</strong>{' '}
                {contract.offerSpecifics.buyerAgentCompensation.toLocaleString()}
                %
              </Typography>
            )}
            {isPositive(
              contract.offerSpecifics?.sellerAssistanceClosingCosts
            ) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Seller Assistance:</strong> $
                {contract.offerSpecifics.sellerAssistanceClosingCosts.toLocaleString()}
              </Typography>
            )}
            {isPositive(contract.offerSpecifics?.additionalSellerCredit) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Additional Seller Credit:</strong> $
                {contract.offerSpecifics.additionalSellerCredit.toLocaleString()}
                %
              </Typography>
            )}
          </Box>

          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            By signing below, you agree to all terms and conditions of this
            contract. Please review the full contract document before signing.
          </Typography>

          <CommonFormControl
            label="Legal Full Name:"
            labelWidth={200}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Enter your legal full name"
              name="name"
              fullWidth
              value={form.name}
              onChange={handleInputChange}
              required
            />
          </CommonFormControl>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Signature:
            </Typography>
            <UserSignatureCanvas
              {...(isBuyer
                ? { buyersignature: form.signature }
                : { sellersignature: form.signature })}
              onSignatureSave={(signature) =>
                setForm((prevForm) => ({ ...prevForm, signature }))
              }
            />
          </Box>

          {submitError && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Signature not recorded</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <CommonPrevNextButtons
        sx={{ mt: 3 }}
        isLoading={isSigning}
        isNextDisabled={!isValid}
        onPrev={() => navigate(-1)}
        onNext={handleSubmit}
        nextText="Sign Contract"
      />
    </Box>
  );
};
